import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import appCssInline from "../styles.css?inline";
import interLatin from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import frauncesLatin from "@fontsource-variable/fraunces/files/fraunces-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { Toaster } from "sonner";
import { SITE } from "@/lib/site";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Sarasota Property Care | Anchor Services" },
      { name: "description", content: "Anchor Services is Sarasota's complete property care company — home watch, junk removal, estate and property cleanouts, storm debris hauling, removals and concierge services." },
      { name: "author", content: SITE.name },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:title", content: "Sarasota Property Care | Anchor Services" },
      { name: "twitter:title", content: "Sarasota Property Care | Anchor Services" },
      { property: "og:description", content: "Complete Sarasota property care — home watch, junk removal, cleanouts, storm debris hauling, removals and concierge services." },
      { name: "twitter:description", content: "Complete Sarasota property care — home watch, junk removal, cleanouts, storm debris hauling, removals and concierge services." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/rIFV7t7da1gHGdnhe3LSx2T4aqw2/social-images/social-1781204949695-social.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/rIFV7t7da1gHGdnhe3LSx2T4aqw2/social-images/social-1781204949695-social.webp" },
    ],
    links: [
      ...(import.meta.env.DEV
        ? [{ rel: "stylesheet", href: appCss }]
        : []),
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interLatin,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: frauncesLatin,
        crossOrigin: "anonymous",
      },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg?v=2" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=2" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png?v=2" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=2" },
      { rel: "manifest", href: "/site.webmanifest?v=2" },
    ],
    styles: import.meta.env.DEV
      ? []
      : [{ children: appCssInline }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://anchorsrq.com/#organization",
          name: "Anchor Services",
          url: "https://anchorsrq.com",
          logo: "https://anchorsrq.com/logo.png",
          telephone: "+1-941-500-2320",
          email: "info@anchorsrq.com",
          description: "Anchor Services provides complete property care in the Sarasota area — home watch, property inspections, junk removal, estate and property cleanouts, garage, foreclosure and eviction cleanouts, storm debris removal, furniture and appliance removal, and concierge services — for homeowners, seasonal residents, realtors, investors, and property managers.",
          sameAs: [
            "https://www.facebook.com/anchorservices",
            "https://www.instagram.com/anchorservices",
            "https://www.linkedin.com/company/anchor-services",
            "https://www.youtube.com/@anchorservices",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex-1 pb-20 lg:pb-0">
          <Outlet />
        </main>
        <SiteFooter />
        <StickyMobileCTA />
        <Toaster richColors position="top-center" />
      </div>
    </QueryClientProvider>
  );
}
